import React, { useRef, lazy, Suspense } from 'react'
import { css } from '@emotion/core'
import {
  Grid,
  Cell,
  PersonBlock,
  useContainerWidth,
  useStatusOverlay,
  BillboardButton,
  Button,
  useOverlay,
  H2,
  colors,
  Paragraph,
  Separator,
  Row,
} from '@fs/zion-ui'
import { ContentDownload, TreeViewRelationship, NoticeLoading } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@fs/zion-analytics'
import html2canvas from 'html2canvas'
import { usePreference } from '@fs/zion-preferences'
import TextRibbon from '../../../TextRibbon/TextRibbon'
import ProfileImage from '../../../ProfileImage/ProfileImage'
import { PersonType } from '../../../../propTypes/personDataProps'
import { getDisplayName } from '../../../../helpers/name_formatting'
import vitalStrings from '../../../../helpers/vitalStrings'
import useDSApplicationMode from '../../../../hooks/useDSApplicationMode'
import RelativeTakeaway from '../../../../../wcf/AppWcf/components/Heritage/takeaways/RelativeTakeaway'
import { useUploadKeepsakeImage } from '../../../../../keepsakes/hooks/hooks'
import { useHasEmail } from '../../../../hooks/useHasEmail'

/* ─────────────────────────────────────────────────────────────
   Styles
───────────────────────────────────────────────────────────── */

const GENDER_COLORS = {
  male: '#7DE3F4',
  female: '#FC4E6D',
  unknown: '#ccccc4',
}

const GENDER_GRADIENTS = {
  male: 'linear-gradient(90deg, #003057 0%, #177c9c 100%)',
  female: 'linear-gradient(90deg, #8f124a 0%, #bd0057 100%)',
  unknown: 'linear-gradient(90deg, #aaaaaa 0%, #dddddd 100%)',
}

const containerCss = css`
  padding: 20px;
  @media (max-width: 823px) and (orientation: landscape) {
    padding: 8px;
  }
  border-bottom: 1px solid ${colors.gray05};
`

const centeredCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
`

const infoCss = css`
  display: inline-block;
  margin: auto 20px;
  @media (max-width: 823px) and (orientation: landscape) {
    margin: auto 0;
  }
`

const lifespanRowCss = css`
  padding-bottom: 10px;
  @media (max-width: 823px) and (orientation: landscape) {
    display: none;
  }
`

const takeawayWrapperCss = css`
  position: fixed;
  width: 1px;
  height: 1px;
  top: 0;
  inset-inline-start: 0;
  overflow: hidden;
  > div {
    width: 750px;
  }
`

const getGenderStripeCss = (gender) => css`
  background: ${GENDER_GRADIENTS[gender] || GENDER_GRADIENTS.unknown};
  background-size: 100% 8px;
  background-repeat: no-repeat;
`

/* ─────────────────────────────────────────────────────────────
   Components
───────────────────────────────────────────────────────────── */

const RelationshipViewer = lazy(() => import('@fs/zion-relationship-viewer/dist/es/RelationshipViewer'))

function RelationRibbon({ relation, gender }) {
  if (!relation) return null
  return (
    <TextRibbon
      type="ribbon-ribbon"
      color="#000"
      backgroundColor={GENDER_COLORS[gender] || GENDER_COLORS.unknown}
      size="14px"
    >
      <span
        css={css`
          font-weight: bold;
          font-size: 12px;
          text-transform: uppercase;
        `}
      >
        {relation}
      </span>
    </TextRibbon>
  )
}

function RelationshipButton({ onClick, label }) {
  return (
    <Button
      Icon={TreeViewRelationship}
      emphasis="low"
      size="xs"
      onClick={onClick}
      aria-label={label}
      data-testid="relationship-viewer-button"
    >
      {label}
    </Button>
  )
}

export default function PersonHeader({ personData }) {
  const atWidth = useContainerWidth()
  const showRoman = usePreference('tree.showNamesInRomanScript')
  const [t] = useTranslation()
  const appMode = useDSApplicationMode()
  const relationshipOverlay = useOverlay()
  const relativeTakeaway = useRef()
  const [uploadFunc, uploadState] = useUploadKeepsakeImage()
  const showStatusOverlay = useStatusOverlay()
  const hasEmailAddress = useHasEmail()

  const displayName = getDisplayName(personData, showRoman)
  const vitals = vitalStrings(personData)

  const person = {
    id: personData?.personId,
    name: displayName ?? 'Unknown',
    gender: personData?.gender,
    relation: personData?.relation,
    lifespan: personData?.lifespan,
    imageLink: personData?.imageLink,
  }

  const openRelationshipViewer = (evt) => {
    trackEvent({ event_name: 'open_relationship_viewer' })
    relationshipOverlay.open(evt)
  }

  const triggerTakeAway = async () => {
    trackEvent({ event_name: 'PersonHeader:SAVE_RELATIVE:CLICK', person_id: person.id })
    const canvas = await html2canvas(relativeTakeaway.current, { useCORS: true })
    uploadFunc({ imgDataURL: canvas.toDataURL('png') })
  }

  // Upload status effect
  React.useEffect(() => {
    if (uploadState.pending) {
      showStatusOverlay({ message: t('station-kiosk.uploading', 'Uploading...') })
    } else if (uploadState.complete && uploadState.error) {
      showStatusOverlay({ message: t('station-kiosk.uploading-failed', 'Something went wrong!'), type: 'error' })
    } else if (uploadState.complete) {
      showStatusOverlay({ message: t('station-kiosk.uploading-complete', 'Upload complete!'), type: 'success' })
    }
  }, [showStatusOverlay, t, uploadState.complete, uploadState.error, uploadState.pending])

  const relationshipButtonLabel = t('person-viewer.view-relationship', 'View Relationship')

  return (
    <div css={getGenderStripeCss(person.gender)}>
      <div css={containerCss}>
        <Grid>
          {/* Mobile layout */}
          <Cell columns={atWidth({ default: 12, sm: 'hide' })}>
            <PersonBlock avatarProps={{ src: person.imageLink, sex: person.gender }} centered size="lg" />
            <H2 size="H5" centered>
              {person.name}
            </H2>
            <div css={centeredCss}>
              <RelationRibbon relation={person.relation} gender={person.gender} />
            </div>
            <Paragraph centered>
              <strong>{vitals?.lifespan}</strong>
            </Paragraph>
            {person.id && (
              <Paragraph size="xs" centered>
                {person.id}
              </Paragraph>
            )}
            <Row alignX="center">
              <RelationshipButton onClick={openRelationshipViewer} label={relationshipButtonLabel} />
            </Row>
          </Cell>

          {/* Desktop layout */}
          <Cell columns={atWidth({ default: 'hide', sm: 12 })}>
            <Grid>
              <Cell columns={atWidth({ sm: 3, xl: 2 })}>
                <ProfileImage
                  gender={person.gender}
                  userImage={person.imageLink}
                  name={person.name}
                  border="6px solid #FFF"
                />
              </Cell>
              <Cell columns={atWidth({ sm: 9, xl: 10 })}>
                <div css={infoCss}>
                  <RelationRibbon relation={person.relation} gender={person.gender} />

                  <H2 size="H2">{person.name}</H2>
                  <div css={lifespanRowCss}>
                    <strong>{vitals?.lifespan}</strong>
                  </div>
                  {person.id && <Paragraph size="xs">{person.id}</Paragraph>}
                  <RelationshipButton onClick={openRelationshipViewer} label={relationshipButtonLabel} />
                  {appMode === 'STATION' && hasEmailAddress && (
                    <>
                      <Separator />
                      <BillboardButton
                        data-testid="person-header-save-relative-billboard-button"
                        Icon={ContentDownload}
                        type="button"
                        onClick={triggerTakeAway}
                        color={person.gender === 'male' ? 'blue' : 'red'}
                      >
                        {t('wcf.heritage.food.save-relative', 'Save Relative')}
                      </BillboardButton>
                    </>
                  )}
                </div>
              </Cell>
            </Grid>
          </Cell>
        </Grid>
      </div>

      {/* Relationship viewer overlay */}
      {(relationshipOverlay.isOpen || relationshipOverlay.transitioning) && person.id && (
        <Suspense fallback={<NoticeLoading size="lg" />}>
          <RelationshipViewer
            toId={person.id}
            overlay={{ ...relationshipOverlay, actions: [] }} // Setting the actions array to empty to hide default help button
            noLinks={appMode !== 'WEB'}
          />
        </Suspense>
      )}

      {/* Hidden takeaway for screenshot (STATION mode only) */}
      <div css={takeawayWrapperCss}>
        <div ref={relativeTakeaway}>
          <RelativeTakeaway
            profile={person.imageLink}
            relation={person.relation}
            heading={`${personData?.name} ${personData?.surname ?? ''}`}
            subHeading={vitals?.lifespan}
            gender={person.gender}
          />
        </div>
      </div>
    </div>
  )
}

PersonHeader.propTypes = {
  personData: PersonType,
}
